import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCompanyAiAgents, usePutCompanyAiAgent } from '@/data/aiAgent';
import { Loading } from '@/components/ui/loading';
import { Error } from '@/components/ui/error';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { mutateListOnPut } from '@/helpers/swr';

const getAgentCounts = (data) => {
  if (!data) return { enabled: 0, total: 0 };
  const agents = data.query.map((uid) => data.content[uid]);
  const enabled = agents.filter((a) => a.is_enabled).length;
  return { enabled, total: agents.length };
};

const AgentList = ({ companyUid, context }) => {
  const companyAiAgents = useCompanyAiAgents(companyUid, context);
  const putCompanyAiAgent = usePutCompanyAiAgent();
  const [togglingUids, setTogglingUids] = useState({});

  const handleToggle = async (agent) => {
    setTogglingUids((prev) => ({ ...prev, [agent.uid]: true }));
    try {
      const updated = await putCompanyAiAgent(agent.uid, { is_enabled: !agent.is_enabled });
      companyAiAgents.mutate((current) => mutateListOnPut(current, { ...agent, ...updated }), false);
    } catch {
      companyAiAgents.mutate();
    } finally {
      setTogglingUids((prev) => ({ ...prev, [agent.uid]: false }));
    }
  };

  if (companyAiAgents.isLoading && !companyAiAgents.data) {
    return <Loading />;
  }

  if (companyAiAgents.error) {
    return <Error message="An error occurred loading company AI agents" />;
  }

  const agents = companyAiAgents.data.query.map((uid) => companyAiAgents.data.content[uid]);

  const categories = [];
  const categoryMap = {};
  agents.forEach((agent) => {
    const categoryUid = agent.category?.uid;
    if (!categoryUid) return;
    if (!categoryMap[categoryUid]) {
      categoryMap[categoryUid] = {
        ...agent.category,
        agents: []
      };
      categories.push(categoryMap[categoryUid]);
    }
    categoryMap[categoryUid].agents.push(agent);
  });

  return (
    <div className="space-y-6">
      {categories.map((category) => (
        <div key={category.uid}>
          <h4 className="text-md font-semibold mb-2">{category.label}</h4>
          <p className="text-sm text-muted-foreground mb-3">{category.description}</p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[100px] text-center">Enabled</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {category.agents.map((agent) => (
                <TableRow key={agent.uid}>
                  <TableCell className="font-medium">{agent.title}</TableCell>
                  <TableCell className="text-muted-foreground">{agent.description}</TableCell>
                  <TableCell className="text-center">
                    <Switch checked={agent.is_enabled} onCheckedChange={() => handleToggle(agent)} disabled={!!togglingUids[agent.uid]} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}
    </div>
  );
};

const AiAgents = () => {
  const params = useParams();
  const [activeContext, setActiveContext] = useState('estimate');
  const estimateAgents = useCompanyAiAgents(params.companyUid, 'estimate');
  const comparisonAgents = useCompanyAiAgents(params.companyUid, 'comparison');

  const estimateCounts = getAgentCounts(estimateAgents.data);
  const comparisonCounts = getAgentCounts(comparisonAgents.data);
  const totalEnabled = estimateCounts.enabled + comparisonCounts.enabled;
  const totalAll = estimateCounts.total + comparisonCounts.total;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">
          AI Agents
          {totalAll > 0 && (
            <span className="text-muted-foreground font-normal text-base ml-2">
              ({totalEnabled}/{totalAll} enabled)
            </span>
          )}
        </h2>
      </div>

      <Tabs value={activeContext} onValueChange={setActiveContext}>
        <TabsList>
          <TabsTrigger value="estimate">Estimate{estimateCounts.total > 0 && ` (${estimateCounts.enabled}/${estimateCounts.total})`}</TabsTrigger>
          <TabsTrigger value="comparison">Comparison{comparisonCounts.total > 0 && ` (${comparisonCounts.enabled}/${comparisonCounts.total})`}</TabsTrigger>
        </TabsList>
      </Tabs>

      <AgentList companyUid={params.companyUid} context={activeContext} />
    </div>
  );
};

export default AiAgents;
